package com.meistercharts.charts.pv.modulePlanning

import com.meistercharts.algorithms.layers.AbstractLayer
import com.meistercharts.algorithms.layers.LayerPaintingContext
import com.meistercharts.algorithms.layers.LayerType
import com.meistercharts.algorithms.layers.linechart.LineStyle
import com.meistercharts.algorithms.painter.FillAndStrokeStyle
import com.meistercharts.algorithms.painter.fillAndStroke
import com.meistercharts.annotations.Domain
import com.meistercharts.annotations.Window
import com.meistercharts.annotations.Zoomed
import com.meistercharts.axis.OrientationAwareDirection
import com.meistercharts.axis.OrientationAwareDirection.Companion.calculate
import com.meistercharts.canvas.ChartSupport
import com.meistercharts.canvas.ConfigurationDsl
import com.meistercharts.canvas.DirtyReason
import com.meistercharts.canvas.events.CanvasKeyEventHandler
import com.meistercharts.canvas.events.CanvasMouseEventHandler
import com.meistercharts.canvas.events.CanvasMouseEventHandlerBroker
import com.meistercharts.canvas.command.CommandExecutor
import com.meistercharts.canvas.interaction.InteractionState
import com.meistercharts.canvas.interaction.ToolReaction
import com.meistercharts.charts.pv.roofPlanning.resizable
import com.meistercharts.charts.pv.roofPlanning.resizableOrNull
import it.neckar.open.kotlin.lang.requireNotNull
import com.meistercharts.canvas.interaction.ElementVisualState
import com.meistercharts.canvas.interaction.HandleRole
import com.meistercharts.canvas.interaction.InteractionTarget
import com.meistercharts.canvas.interaction.InteractionTargets
import com.meistercharts.canvas.paintable.Paintable
import com.meistercharts.canvas.resize.ResizeHandler
import com.meistercharts.canvas.resizeHandlesSupport
import com.meistercharts.canvas.saved
import com.meistercharts.charts.pv.roofPlanning.Clickable
import com.meistercharts.charts.pv.roofPlanning.Module
import com.meistercharts.charts.pv.roofPlanning.ModuleArea
import com.meistercharts.charts.pv.roofPlanning.ModuleAreaPainter
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningLayer
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningLayer.Mode
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningModel
import com.meistercharts.charts.pv.roofPlanning.RoofRelative
import com.meistercharts.charts.pv.roofPlanning.UnusableArea
import com.meistercharts.charts.pv.roofPlanning.UnusableAreaPainter
import com.meistercharts.color.Color
import com.meistercharts.color.withAlpha
import com.meistercharts.events.EventConsumption
import com.meistercharts.events.EventConsumption.Ignored
import com.meistercharts.events.gesture.CanvasDragSupport
import com.meistercharts.events.gesture.connectedMouseEventHandler
import com.meistercharts.range.LinearValueRange
import com.meistercharts.resources.Icons
import it.neckar.events.KeyUpEvent
import it.neckar.events.MouseDownEvent
import it.neckar.events.MouseMoveEvent
import it.neckar.geometry.Coordinates
import it.neckar.geometry.Direction
import it.neckar.geometry.Distance
import it.neckar.geometry.Rectangle
import it.neckar.geometry.Size
import it.neckar.open.collections.fastForEach
import it.neckar.open.dispose.disposeOn
import it.neckar.open.observable.ObservableObject
import it.neckar.open.unit.si.mm
import it.neckar.open.unit.si.ms

/**
 * Layer that supports planning of  photo voltaic modules for *one* roof
 */
class PvModulePlanningLayer(
  val configuration: Configuration,
  additionalConfiguration: Configuration.() -> Unit = {},
) : AbstractLayer() {

  constructor(
    pvRoofPlanningLayer: PvRoofPlanningLayer,
    additionalConfiguration: Configuration.() -> Unit = {},
  ) : this(Configuration(pvRoofPlanningLayer), additionalConfiguration)

  init {
    configuration.additionalConfiguration()
  }

  override val type: LayerType = LayerType.Content

  /**
   * The interactive regions of this layer: the bodies of the module areas and unusable areas, and the buttons painted on top of them.
   *
   * Filled in [layout], queried by the mouse handlers - so hover and press always resolve to the same region.
   */
  private val interactionTargets: InteractionTargets<Clickable> = InteractionTargets()

  /**
   * Decides what the mouse does. Never changes the model - it returns commands.
   */
  private val tool: RoofPlanningTool = RoofPlanningTool(toRoofDistance = ::toRoofDistance)

  /**
   * Applies the commands the tool asks for and keeps the undo history.
   */
  val commandExecutor: CommandExecutor<PvRoofPlanningModel> = CommandExecutor(configuration.model)

  private val interactionStateProperty: ObservableObject<InteractionState<Clickable>> = ObservableObject(InteractionState.empty())

  /**
   * What the user is currently doing with the roof.
   */
  var interactionState: InteractionState<Clickable> by interactionStateProperty
    private set

  /**
   * The chart support of the running interaction. Set on initialize - it carries the chart calculator every conversion
   * into roof coordinates needs, and marks the canvas as dirty.
   */
  private var chartSupport: ChartSupport? = null

  /**
   * Converts a distance on screen into a distance on the roof.
   */
  private fun toRoofDistance(distance: @Zoomed Distance): @RoofRelative @mm Distance {
    val chartCalculator = chartSupport.requireNotNull { "The layer has not been initialized yet" }.chartCalculator

    return Distance(
      configuration.getValueRangeX().deltaToDomain(chartCalculator.zoomedDelta2domainRelativeX(distance.x)),
      configuration.getValueRangeY().deltaToDomain(chartCalculator.zoomedDelta2domainRelativeY(distance.y)),
    )
  }

  /**
   * Applies what the tool answered: the new interaction state and the model changes it asked for.
   */
  private fun apply(reaction: ToolReaction<Clickable, PvRoofPlanningModel>) {
    interactionState = reaction.state
    commandExecutor.execute(reaction.commands)

    if (reaction.commands.isNotEmpty()) {
      chartSupport?.markAsDirty(DirtyReason.UserInteraction)
    }
  }

  /**
   * Applies the reaction and answers the event with the consumption the tool asked for.
   */
  private fun handle(reaction: ToolReaction<Clickable, PvRoofPlanningModel>): EventConsumption {
    apply(reaction)
    return reaction.consumption
  }

  /**
   * What the tool answers to a press. The press ends the gesture before it in the undo history as well - a release
   * outside the canvas never reaches this layer.
   */
  private fun pressed(target: InteractionTarget<Clickable>?, location: @Window Coordinates): ToolReaction<Clickable, PvRoofPlanningModel> {
    commandExecutor.finishGesture()
    return tool.onPress(target, location, interactionState, configuration.model)
  }

  /**
   * The drag support is registered as first mouse handler
   */
  private val dragSupport: CanvasDragSupport = CanvasDragSupport().also {
    it.handle(object : CanvasDragSupport.Handler {
      override fun isDraggingAllowedFromHere(source: CanvasDragSupport, location: @Window Coordinates, chartSupport: ChartSupport): Boolean {
        val target = interactionTargets.findTargetAt(location)
        if (target?.role != HandleRole.Body) {
          return false
        }

        apply(pressed(target, location))
        return true
      }

      override fun onDrag(source: CanvasDragSupport, @Window location: Coordinates, @Zoomed distance: Distance, @ms deltaTime: Double, chartSupport: ChartSupport): EventConsumption {
        return handle(tool.onDrag(source.totalDistanceTo(location), interactionState, configuration.model))
      }

      override fun onFinish(source: CanvasDragSupport, location: Coordinates, chartSupport: ChartSupport): EventConsumption {
        commandExecutor.finishGesture()
        return handle(tool.onRelease(interactionState, configuration.model))
      }
    })
  }

  override val mouseEventHandler: CanvasMouseEventHandler = CanvasMouseEventHandlerBroker().also { broker ->
    /**
     * A press on a button is handled before the drag support sees it, so pressing a button never starts a drag
     */
    broker.delegate(object : CanvasMouseEventHandler {
      override fun onDown(event: MouseDownEvent, chartSupport: ChartSupport): EventConsumption {
        val target = interactionTargets.findTargetAt(event.coordinates)
        if (target == null || target.role == HandleRole.Body) {
          //Nothing or a body - the drag support and the handler below take over
          return Ignored
        }

        return handle(pressed(target, event.coordinates))
      }
    })

    /**
     * Handle dragging now
     */
    dragSupport.connectedMouseEventHandler().let {
      broker.delegate(it)
    }

    /**
     * All other events are handled here
     */
    broker.delegate(object : CanvasMouseEventHandler {
      override fun onMove(event: MouseMoveEvent, chartSupport: ChartSupport): EventConsumption {
        val coordinates = event.coordinates
        val target = coordinates?.let { interactionTargets.findTargetAt(it) }

        return handle(tool.onHover(target, coordinates, interactionState, configuration.model))
      }

      override fun onDown(event: MouseDownEvent, chartSupport: ChartSupport): EventConsumption {
        //Reached only when neither a button nor a body was hit: the press clears the selection
        return handle(pressed(null, event.coordinates))
      }
    })
  }

  override val keyEventHandler: CanvasKeyEventHandler = object : CanvasKeyEventHandler {
    override fun onUp(event: KeyUpEvent, chartSupport: ChartSupport): EventConsumption {
      return handle(tool.onKey(event.keyStroke, interactionState, configuration.model))
    }
  }

  override fun initialize(paintingContext: LayerPaintingContext) {
    val chartSupport = paintingContext.chartSupport
    this.chartSupport = chartSupport

    //Update the mouse cursor depending on the state - tied to the chart-support lifecycle
    interactionStateProperty.consumeImmediately {
      chartSupport.cursor = tool.cursorFor(it)
      chartSupport.markAsDirty(DirtyReason.UiStateChanged)
    }.disposeOn(chartSupport)

    chartSupport.resizeHandlesSupport.onResize(this, object : ResizeHandler {
      override fun beginResizing(handleDirection: Direction) {
        apply(tool.startResizing(interactionState))
      }

      override fun resizingFinished() {
        commandExecutor.finishGesture()
        apply(tool.onRelease(interactionState, configuration.model))
      }

      override fun resizing(totalDistance: Distance, handleDirection: Direction, totalX: Double, totalY: Double) {
        //The delete key ends the resize, and the drag events until the next layout still arrive - they belong to no gesture
        val resize = interactionState.resized ?: return
        val resizable = resize.element.resizable()

        val chartCalculator = chartSupport.chartCalculator
        @Domain val deltaXDomain = configuration.getValueRangeX().deltaToDomain(chartCalculator.zoomedDelta2domainRelativeX(totalX))
        @Domain val deltaYDomain = configuration.getValueRangeY().deltaToDomain(chartCalculator.zoomedDelta2domainRelativeY(totalY))

        //Depending on the axis orientation and the handle, a drag grows the element or moves its origin
        val directionX: OrientationAwareDirection = OrientationAwareDirection.calculate(chartCalculator.chartState.axisOrientationX, handleDirection.horizontalAlignment)
        val directionY: OrientationAwareDirection = OrientationAwareDirection.calculate(chartCalculator.chartState.axisOrientationY, handleDirection.verticalAlignment)

        val towardsSmallerX = directionX == OrientationAwareDirection.TowardsSmaller
        val towardsSmallerY = directionY == OrientationAwareDirection.TowardsSmaller

        val resizedBounds = resizable.resizedBy(
          anchor = resize.from,
          deltaLocationX = if (towardsSmallerX) deltaXDomain else 0.0,
          deltaLocationY = if (towardsSmallerY) deltaYDomain else 0.0,
          deltaWidth = if (towardsSmallerX) -deltaXDomain else deltaXDomain,
          deltaHeight = if (towardsSmallerY) -deltaYDomain else deltaYDomain,
          model = configuration.model,
        )

        apply(tool.resizeTo(resizedBounds.location, resizedBounds.size, interactionState))
      }
    })
  }

  override fun layout(paintingContext: LayerPaintingContext) {
    //super.layout runs initialize, which registers the resize handler that setResizable below requires
    super.layout(paintingContext)

    registerInteractionTargets(paintingContext)

    val resizeHandlesSupport = paintingContext.chartSupport.resizeHandlesSupport

    interactionState.selected.resizableOrNull()?.let { resizable ->
      val resizableBounds: @Window Rectangle = when (resizable) {

        is UnusableArea -> configuration.getBoundsForUnusableArea(resizable)

        is ModuleArea -> {
          val chartCalculator = paintingContext.chartCalculator
          val valueRangeX = configuration.getValueRangeX()
          val valueRangeY = configuration.getValueRangeY()

          val x = chartCalculator.domain2windowX(resizable.location.x, valueRangeX)
          val y = chartCalculator.domain2windowY(resizable.location.y, valueRangeY)
          val width = chartCalculator.domainDelta2zoomedX(resizable.size.width, valueRangeX)
          val height = chartCalculator.domainDelta2zoomedY(resizable.size.height, valueRangeY)

          Rectangle(x, y, width, height)
        }

      }
      resizeHandlesSupport.setResizable(this, resizableBounds, paintingContext.loopIndex)
    } ?: resizeHandlesSupport.clear(this)
  }

  /**
   * Registers every interactive region of this layer.
   *
   * The registration order is the paint order, because a region registered later wins over an earlier one of the same
   * [HandleRole.hitPriority]: module areas first, then the unusable areas painted on top of them, then the buttons.
   */
  private fun registerInteractionTargets(paintingContext: LayerPaintingContext) {
    interactionTargets.beginLayout()

    val model = configuration.model

    model.moduleAreas.moduleAreas.fastForEach { moduleArea ->
      interactionTargets.register(moduleArea, HandleRole.Body, configuration.getBoundsForModuleArea(moduleArea))
    }

    model.unusableAreas.unusableAreas.fastForEach { unusableArea ->
      interactionTargets.register(unusableArea, HandleRole.Body, configuration.getBoundsForUnusableArea(unusableArea), unusableArea.rightTriangleType)
    }

    val moduleDeleteIconBounds = configuration.deleteIcon.boundingBox(paintingContext)
    model.modules().visibleModules.fastForEach { module ->
      interactionTargets.registerCentered(module, HandleRole.DeleteButton, configuration.getBoundsForModule(module), moduleDeleteIconBounds)
    }

    val unusableAreaDeleteIconBounds = configuration.deleteIcon.boundingBox(paintingContext)
    model.unusableAreas.unusableAreas.fastForEach { unusableArea ->
      interactionTargets.registerCentered(unusableArea, HandleRole.DeleteButton, configuration.getBoundsForUnusableArea(unusableArea), unusableAreaDeleteIconBounds)
    }

    val moduleAreaDeleteIconBounds = configuration.moduleAreaPainter.configuration.deleteIcon.boundingBox(paintingContext)
    val moduleAreaRotateIconBounds = configuration.moduleAreaPainter.configuration.rotateIcon.boundingBox(paintingContext)
    model.moduleAreas.moduleAreas.fastForEach { moduleArea ->
      val moduleAreaBounds = configuration.getBoundsForModuleArea(moduleArea)

      interactionTargets.registerCentered(moduleArea, HandleRole.DeleteButton, moduleAreaBounds, moduleAreaDeleteIconBounds)

      //The rotate button sits at the top right corner of the module area
      interactionTargets.register(
        element = moduleArea,
        role = HandleRole.RotateButton,
        x = moduleAreaBounds.getX() + moduleAreaBounds.getWidth() - moduleAreaRotateIconBounds.getWidth() + moduleAreaRotateIconBounds.getX(),
        y = moduleAreaBounds.getY() + moduleAreaBounds.getHeight() + moduleAreaRotateIconBounds.getHeight() + moduleAreaRotateIconBounds.getY(),
        width = moduleAreaRotateIconBounds.getWidth(),
        height = moduleAreaRotateIconBounds.getHeight(),
      )
    }
  }

  /**
   * Registers an icon sized region in the center of the given element bounds.
   */
  private fun InteractionTargets<Clickable>.registerCentered(
    element: Clickable,
    role: HandleRole,
    elementBounds: @Window Rectangle,
    iconBounds: @Zoomed Rectangle,
  ) {
    register(
      element = element,
      role = role,
      x = elementBounds.centerX + iconBounds.getX(),
      y = elementBounds.centerY + iconBounds.getY(),
      width = iconBounds.getWidth(),
      height = iconBounds.getHeight(),
    )
  }

  override fun paint(paintingContext: LayerPaintingContext) {
    val gc = paintingContext.gc
    val chartCalculator = paintingContext.chartCalculator
    val model = configuration.model

    val modules = model.modules()
    modules.visibleModules.fastForEach { module ->
      paintModuleHighlight(paintingContext, module, interactionState.highlightOf(module, modules.overlaps(module)))
      paintModuleIcon(paintingContext, module, interactionState.iconOf(module))
    }

    model.unusableAreas.unusableAreas.fastForEach { unusableArea ->
      val visualState = interactionState.visualStateOf(unusableArea)
      if (visualState == ElementVisualState.Normal) {
        //The roof layer below already paints the unusable area - nothing to add
        return@fastForEach
      }

      gc.saved {
        val bounds = configuration.getBoundsForUnusableArea(unusableArea)
        configuration.unusableAreaPainter.paint(paintingContext, bounds.getX(), bounds.getY(), bounds.getWidth(), bounds.getHeight(), unusableArea, visualState)
      }
    }

    model.moduleAreas.moduleAreas.fastForEach { moduleArea ->
      gc.saved {
        val bounds = configuration.getBoundsForModuleArea(moduleArea)
        configuration.moduleAreaPainter.paint(paintingContext, bounds.getX(), bounds.getY(), bounds.getWidth(), bounds.getHeight(), moduleArea, chartCalculator, interactionState.visualStateOf(moduleArea))
      }
    }
  }

  /**
   * Fills the module with the color that belongs to the given highlight.
   */
  private fun paintModuleHighlight(paintingContext: LayerPaintingContext, module: Module, highlight: ModuleHighlight) {
    val style = when (highlight) {
      ModuleHighlight.None -> return
      ModuleHighlight.Overlapping -> configuration.deleteArmedHighlightStyle
      ModuleHighlight.Hover -> configuration.hoverHighlightStyle
      ModuleHighlight.Dragging -> configuration.draggingHighlightStyle
      ModuleHighlight.DeleteArmed -> configuration.deleteArmedHighlightStyle
      ModuleHighlight.UndeleteArmed -> configuration.undeleteArmedHighlightStyle
    }

    val gc = paintingContext.gc
    val bounds = configuration.getBoundsForModule(module)

    gc.fillAndStroke(style)
    gc.fillRect(bounds)
    gc.strokeRect(bounds)
  }

  /**
   * Paints the icon that belongs to the given module in its center.
   */
  private fun paintModuleIcon(paintingContext: LayerPaintingContext, module: Module, icon: ModuleIcon) {
    val paintable = when (icon) {
      ModuleIcon.None -> return
      ModuleIcon.Delete -> configuration.deleteIcon
      ModuleIcon.DeleteArmed -> configuration.deleteIconArmed
      ModuleIcon.UndeleteArmed -> configuration.undeleteIconArmed
    }

    val bounds = configuration.getBoundsForModule(module)

    paintingContext.gc.saved {
      paintable.paint(paintingContext, bounds.centerX, bounds.centerY)
    }
  }

  @ConfigurationDsl
  class Configuration(
    /**
     * Paints the roof below this layer and owns the geometry every region is derived from.
     */
    val roofPlanningLayer: PvRoofPlanningLayer,
  ) {
    val model: PvRoofPlanningModel
      get() = roofPlanningLayer.configuration.model

    /**
     * The current mode. Shared with [roofPlanningLayer] so both layers can never disagree about it.
     */
    var mode: Mode
      get() = roofPlanningLayer.configuration.mode
      set(value) {
        roofPlanningLayer.configuration.mode = value
      }

    val planningEnabled: Boolean
      get() = roofPlanningLayer.configuration.planningEnabled

    fun getValueRangeX(): @Window LinearValueRange = roofPlanningLayer.getValueRangeX()

    fun getValueRangeY(): @Window LinearValueRange = roofPlanningLayer.getValueRangeY()

    fun getBoundsForModuleArea(moduleArea: ModuleArea): @Window Rectangle = roofPlanningLayer.getBoundsForModuleArea(moduleArea)

    fun getBoundsForModule(module: Module): @Window Rectangle = roofPlanningLayer.getBoundsForModule(module)

    fun getBoundsForUnusableArea(unusableArea: UnusableArea): @Window Rectangle = roofPlanningLayer.getBoundsForUnusableArea(unusableArea)

    /**
     * The line style for the roof insets
     */
    val suggestedRoofInsets: LineStyle = LineStyle(Color.red)

    /**
     * The style when the mouse over an element
     */
    val hoverHighlightStyle: FillAndStrokeStyle = FillAndStrokeStyle(Color.web("#75b72633"), Color.web("#75b726"))

    /**
     * When hovering above the delete button
     */
    val deleteArmedHighlightStyle: FillAndStrokeStyle = FillAndStrokeStyle(Color.red().withAlpha(0.3), Color.red())

    val undeleteArmedHighlightStyle: FillAndStrokeStyle = FillAndStrokeStyle(Color.green().withAlpha(0.3), Color.green())

    /**
     * Highlighting during drag operation
     */
    val draggingHighlightStyle: FillAndStrokeStyle = FillAndStrokeStyle(Color.white.withAlpha(0.2), Color.white)

    /**
     * The delete icon that is painted on hover
     */
    val deleteIcon: Paintable = Icons.delete(Size.PX_24)

    /**
     * The delete icon while hovering above it
     */
    val deleteIconArmed: Paintable = Icons.delete(Size.PX_24, Color.red)

    val undeleteIconArmed: Paintable = Icons.delete(Size.PX_24, Color.green)

    /**
     * The paintable for unusable areas
     */
    var unusableAreaPainter: UnusableAreaPainter = UnusableAreaPainter()

    var moduleAreaPainter: ModuleAreaPainter = ModuleAreaPainter()
  }
}
